import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";

// Self-hosted demo video player — no YouTube/Vimeo, no third-party branding,
// no upload date or view count exposed. Point `videoUrl` at your R2 public
// bucket URL (or a Cloudflare-CDN'd R2 URL) once the file is exported.
//
// Usage:
//   <DemoVideoPlayer
//     videoUrl="https://videos.checkoutdm.com/demo.mp4"
//     posterUrl="https://images.checkoutdm.com/static/demo-poster.jpg"
//     title="See Checkout DM in action"
//   />
export default function DemoVideoPlayer({
  videoUrl,
  posterUrl,
  title = "Product demo",
  className = "",
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
    // Video element only mounts src on click (preload="none" below), so give
    // the browser a tick before calling play().
    requestAnimationFrame(() => {
      videoRef.current?.play();
    });
  };

  return (
    <div
      className={`relative overflow-hidden bg-ink ${
        className ||
        "mx-auto aspect-video w-full max-w-3xl rounded-[2rem] border border-ink/10 shadow-sm"
      }`}
    >
      <video
        ref={videoRef}
        src={isPlaying ? videoUrl : undefined}
        poster={posterUrl}
        preload="none"
        controls={isPlaying}
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        playsInline
        title={title}
        className="h-full w-full object-cover"
        onPause={() => {
          /* keep controls visible on pause; no-op */
        }}
        onEnded={() => setIsPlaying(false)}
      />

      <AnimatePresence>
        {!isPlaying && (
          <motion.button
            type="button"
            onClick={handlePlay}
            aria-label={`Play video: ${title}`}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink/20 transition-colors hover:bg-ink/30"
          >
            <span className="relative grid h-20 w-20 place-items-center">
              {/* expanding "sonar ping" rings — pure CSS animation, no video bytes involved */}
              <motion.span
                className="absolute inset-0 rounded-full bg-white/50"
                animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              />
              <motion.span
                className="absolute inset-0 rounded-full bg-white/50"
                animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 0.6,
                }}
              />
              <motion.span
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="relative grid h-20 w-20 place-items-center rounded-full bg-gradient-to-tr from-[#FF3366] via-[#FF7733] to-[#FFB347] shadow-[0_0_25px_rgba(255,51,102,0.5)]"
              >
                <Play size={28} className="ml-1 text-white" fill="white" />
              </motion.span>
            </span>
            <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-ink shadow-sm">
              Tap to watch
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
